// Pipeline-safe config — loaded from config.json, secrets injected by CI before prod deployment.
export interface AppConfig {
  MSAL_CONFIG_CLIENT_ID: string;
  MSAL_CONFIG_TENANT_ID: string;
  MSAL_CONFIG_AUTHORITY: string;
  MSAL_REDIRECT_URI: string;
  ADV_CLIENT_ID: string;
  BASE_URL: string;
  IDLE_TIMEOUT_MINUTES: number;
  MSAL_CONFIG_SCOPE: string;
}

// Debug-only overrides — loaded from config.debug.json (gitignored, never reaches pipeline).
export interface DebugConfig {
  TENANT_ADAPTER_MODE: 'local' | 'remote';
  MIDDLEWARE_MODE: 'local' | 'remote';
  // Full base URLs used when MODE is 'local'. Must include the path prefix (e.g. /tenantadapters).
  TENANT_ADAPTER_URL: string;
  MIDDLEWARE_URL: string;
  LOCAL_ROLES: string;
}

export type RuntimeConfig = AppConfig & Partial<DebugConfig>;

let cached: RuntimeConfig | null = null;

export const loadConfig = async (): Promise<RuntimeConfig> => {
  if (cached) return cached;

  const baseRes = await fetch('/config.json');
  if (!baseRes.ok) throw new Error(`Failed to load config.json: ${baseRes.status}`);
  const base = await baseRes.json() as AppConfig;

  if (import.meta.env.MODE === 'production') {
    cached = base;
    return cached;
  }

  try {
    const debugRes = await fetch('/config.debug.json');
    if (debugRes.ok) {
      const debug = await debugRes.json() as Partial<DebugConfig>;
      cached = { ...base, ...debug };
    } else {
      console.warn('[Config] config.debug.json not found — running with base config only');
      cached = base;
    }
  } catch {
    console.warn('[Config] Failed to load config.debug.json — running with base config only');
    cached = base;
  }

  return cached;
};

export const getConfig = (): RuntimeConfig => {
  if (!cached) throw new Error('Config not loaded. Call loadConfig() before getConfig().');
  return cached;
};
